import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAdminAuth } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Search, 
  Users, 
  FileText, 
  MessageSquare, 
  CreditCard, 
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  Send,
  Link,
  Unlink,
  RotateCcw,
  StickyNote,
  Activity
} from "lucide-react";

export default function AdminAgentSupport() {
  const { admin } = useAdminAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  
  // Workspace detail state
  const [workspaceDetail, setWorkspaceDetail] = useState(null);
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    workspace_id: "",
    event_type: "",
    limit: 100,
  });
  
  // Failures state
  const [failures, setFailures] = useState([]);
  
  // Support notes state
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("info");
  
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Load dashboard
  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/agent/support/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboard(response.data);
    } catch (error) {
      toast.error("Failed to load support dashboard");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Search workspaces
  const searchWorkspaces = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/agent/support/workspaces/search`, {
        headers: getAuthHeaders(),
        params: { query, limit: 20 }
      });
      setSearchResults(response.data.workspaces || []);
    } catch (error) {
      toast.error("Search failed");
    }
  }, [getAuthHeaders]);

  // Load workspace detail
  const loadWorkspaceDetail = useCallback(async (workspaceId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/agent/support/workspaces/${workspaceId}`, {
        headers: getAuthHeaders()
      });
      setWorkspaceDetail(response.data);
      setSelectedWorkspace(workspaceId);
    } catch (error) {
      toast.error("Failed to load workspace details");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Load audit logs
  const loadAuditLogs = useCallback(async () => {
    try {
      const params = {};
      if (auditFilters.workspace_id) params.workspace_id = auditFilters.workspace_id;
      if (auditFilters.event_type) params.event_type = auditFilters.event_type;
      params.limit = auditFilters.limit;
      
      const response = await axios.get(`${API}/admin/agent/support/audit`, {
        headers: getAuthHeaders(),
        params
      });
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      toast.error("Failed to load audit logs");
    }
  }, [getAuthHeaders, auditFilters]);

  // Load failures
  const loadFailures = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/agent/support/failures`, {
        headers: getAuthHeaders(),
        params: { limit: 100 }
      });
      setFailures(response.data.failures || []);
    } catch (error) {
      toast.error("Failed to load failures");
    }
  }, [getAuthHeaders]);

  // Channel actions
  const performChannelAction = async (workspaceId, channel, action) => {
    try {
      const response = await axios.post(`${API}/admin/agent/support/channels/action`, {
        workspace_id: workspaceId,
        channel,
        action,
        reason: `Admin action by ${admin?.email}`
      }, { headers: getAuthHeaders() });
      
      toast.success(response.data.message);
      
      // Reload workspace detail
      if (selectedWorkspace === workspaceId) {
        loadWorkspaceDetail(workspaceId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  // Document actions
  const performDocumentAction = async (workspaceId, documentId, action) => {
    try {
      const response = await axios.post(`${API}/admin/agent/support/documents/action`, {
        workspace_id: workspaceId,
        document_id: documentId,
        action
      }, { headers: getAuthHeaders() });
      
      toast.success(response.data.message);
      
      // Reload workspace detail
      if (selectedWorkspace === workspaceId) {
        loadWorkspaceDetail(workspaceId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  // Create support note
  const createSupportNote = async () => {
    if (!noteText.trim() || !selectedWorkspace) return;
    
    try {
      await axios.post(`${API}/admin/agent/support/notes`, {
        workspace_id: selectedWorkspace,
        note_type: noteType,
        note_text: noteText
      }, { headers: getAuthHeaders() });
      
      toast.success("Note created");
      setNoteText("");
      
      // Reload workspace detail
      loadWorkspaceDetail(selectedWorkspace);
    } catch (error) {
      toast.error("Failed to create note");
    }
  };

  // Initial load
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchWorkspaces(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchWorkspaces]);

  // Health badge
  const HealthBadge = ({ health }) => {
    const colors = {
      healthy: "bg-green-500/20 text-green-400 border-green-500/30",
      at_risk: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      critical: "bg-red-500/20 text-red-400 border-red-500/30"
    };
    return (
      <Badge className={`${colors[health] || colors.critical} border`}>
        {health?.replace("_", " ") || "unknown"}
      </Badge>
    );
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const colors = {
      active: "bg-green-500/20 text-green-400 border-green-500/30",
      trialing: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      past_due: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      canceled: "bg-red-500/20 text-red-400 border-red-500/30",
      none: "bg-gray-500/20 text-gray-400 border-gray-500/30"
    };
    return (
      <Badge className={`${colors[status] || colors.none} border`}>
        {status || "none"}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Contract Expert Support</h1>
          <p className="text-muted-foreground">Phase 7: Admin Support Tools</p>
        </div>
        <Button onClick={loadDashboard} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50 border border-border">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Activity className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="search" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Search className="w-4 h-4 mr-2" />
            Workspace Lookup
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <FileText className="w-4 h-4 mr-2" />
            Audit Logs
          </TabsTrigger>
          <TabsTrigger value="failures" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Failures
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboard && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Active Trials</p>
                      <p className="text-2xl font-bold text-foreground">{dashboard.active_trials}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <Clock className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Expiring Trials</p>
                      <p className="text-2xl font-bold text-foreground">{dashboard.expiring_trials?.count || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/20 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Recent Failures</p>
                      <p className="text-2xl font-bold text-foreground">{dashboard.recent_failures}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <StickyNote className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Support Notes</p>
                      <p className="text-2xl font-bold text-foreground">{dashboard.recent_support_notes}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Expiring Trials */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-400" />
                    Expiring Trials (Next 3 Days)
                  </h3>
                  {dashboard.expiring_trials?.items?.length > 0 ? (
                    <div className="space-y-2">
                      {dashboard.expiring_trials.items.map((trial, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-2 bg-muted/50 rounded cursor-pointer hover:bg-muted"
                          onClick={() => {
                            loadWorkspaceDetail(trial.workspace_id);
                            setActiveTab("search");
                          }}
                        >
                          <div>
                            <p className="text-sm font-medium text-foreground">{trial.workspace_name}</p>
                            <p className="text-xs text-muted-foreground">{trial.owner_email}</p>
                          </div>
                          <Badge variant="outline" className="text-orange-400 border-orange-400/30">
                            {new Date(trial.trial_end).toLocaleDateString()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No expiring trials</p>
                  )}
                </div>

                {/* Failure Summary */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    Failure Summary
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Failed Indexing</span>
                      <Badge variant={dashboard.failed_indexing_jobs > 0 ? "destructive" : "outline"}>
                        {dashboard.failed_indexing_jobs}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Failed Channels</span>
                      <Badge variant={dashboard.failed_channel_connections > 0 ? "destructive" : "outline"}>
                        {dashboard.failed_channel_connections}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Inactive Workspaces</span>
                      <Badge variant={dashboard.inactive_workspaces_count > 0 ? "secondary" : "outline"}>
                        {dashboard.inactive_workspaces_count}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Onboarding Dropoffs</span>
                      <Badge variant={dashboard.onboarding_dropoffs > 0 ? "secondary" : "outline"}>
                        {dashboard.onboarding_dropoffs}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* Workspace Lookup Tab */}
        <TabsContent value="search" className="space-y-6">
          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by workspace name, owner email, or workspace ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Search Results */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-semibold text-foreground mb-4">Search Results</h3>
              {searchResults.length > 0 ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {searchResults.map((ws) => (
                    <div
                      key={ws.workspace_id}
                      className={`p-3 rounded-lg border cursor-pointer transition ${
                        selectedWorkspace === ws.workspace_id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-muted-foreground/50 hover:bg-muted/50'
                      }`}
                      onClick={() => loadWorkspaceDetail(ws.workspace_id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-foreground">{ws.workspace_name}</span>
                        <HealthBadge health={ws.health_score} />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{ws.owner?.email}</span>
                        <StatusBadge status={ws.subscription_status} />
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span>{ws.document_count} docs</span>
                        <span>{ws.conversation_count} chats</span>
                        <span>{ws.user_count} users</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchQuery ? (
                <p className="text-sm text-muted-foreground">No results found</p>
              ) : (
                <p className="text-sm text-muted-foreground">Enter a search term to find workspaces</p>
              )}
            </div>

            {/* Workspace Detail */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-semibold text-foreground mb-4">Workspace Detail</h3>
              {workspaceDetail ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {/* Overview */}
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{workspaceDetail.workspace_name}</span>
                      <HealthBadge health={workspaceDetail.health_score} />
                    </div>
                    <p className="text-xs text-muted-foreground font-mono mb-2">{workspaceDetail.workspace_id}</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Plan:</span>
                        <span className="ml-2 text-foreground">{workspaceDetail.plan}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <StatusBadge status={workspaceDetail.subscription_status} />
                      </div>
                    </div>
                  </div>

                  {/* Owner Info */}
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium text-foreground mb-2">Owner</h4>
                    <p className="text-sm text-foreground">{workspaceDetail.owner?.name}</p>
                    <p className="text-xs text-muted-foreground">{workspaceDetail.owner?.email}</p>
                  </div>

                  {/* Onboarding Status */}
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium text-foreground mb-2">Onboarding Status</h4>
                    <div className="space-y-1">
                      {workspaceDetail.onboarding_status && Object.entries(workspaceDetail.onboarding_status).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          {value ? (
                            <CheckCircle className="w-3 h-3 text-green-400" />
                          ) : (
                            <XCircle className="w-3 h-3 text-muted-foreground" />
                          )}
                          <span className="text-xs text-muted-foreground">{key.replace(/_/g, ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Channel Status */}
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium text-foreground mb-2">Channels</h4>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          <span className="text-sm">Telegram</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {workspaceDetail.channel_status?.telegram ? (
                            <>
                              <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">Connected</Badge>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => performChannelAction(workspaceDetail.workspace_id, 'telegram', 'disconnect')}
                              >
                                <Unlink className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Badge variant="outline">Not Connected</Badge>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => performChannelAction(workspaceDetail.workspace_id, 'telegram', 'regenerate_qr')}
                              >
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          <span className="text-sm">WhatsApp</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {workspaceDetail.channel_status?.whatsapp ? (
                            <>
                              <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">Connected</Badge>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => performChannelAction(workspaceDetail.workspace_id, 'whatsapp', 'disconnect')}
                              >
                                <Unlink className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <Badge variant="outline">Not Connected</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Documents */}
                  {workspaceDetail.recent_uploads?.length > 0 && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <h4 className="text-sm font-medium text-foreground mb-2">Recent Documents</h4>
                      <div className="space-y-1">
                        {workspaceDetail.recent_uploads.slice(0, 5).map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <FileText className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate text-muted-foreground">{doc.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {doc.embedding_status}
                              </Badge>
                              {doc.embedding_status === 'failed' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => performDocumentAction(workspaceDetail.workspace_id, doc.id, 'reindex')}
                                >
                                  <RotateCcw className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Support Notes */}
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium text-foreground mb-2">Support Notes</h4>
                    <div className="space-y-2 mb-3">
                      {workspaceDetail.support_notes?.length > 0 ? (
                        workspaceDetail.support_notes.slice(0, 5).map((note) => (
                          <div key={note.id} className="p-2 bg-background rounded border border-border">
                            <div className="flex items-center justify-between mb-1">
                              <Badge variant="outline" className="text-xs">{note.note_type}</Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(note.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-foreground">{note.note_text}</p>
                            <p className="text-xs text-muted-foreground mt-1">- {note.author_email}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">No support notes</p>
                      )}
                    </div>
                    
                    {/* Add Note Form */}
                    <div className="space-y-2">
                      <select
                        value={noteType}
                        onChange={(e) => setNoteType(e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded text-sm"
                      >
                        <option value="info">Info</option>
                        <option value="issue">Issue</option>
                        <option value="resolution">Resolution</option>
                        <option value="followup">Follow-up</option>
                        <option value="warning">Warning</option>
                      </select>
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a support note..."
                        className="w-full px-3 py-2 bg-background border border-border rounded text-sm min-h-[60px]"
                      />
                      <Button onClick={createSupportNote} size="sm" className="w-full">
                        <StickyNote className="w-3 h-3 mr-2" />
                        Add Note
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select a workspace to view details</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit" className="space-y-6">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <Input
              placeholder="Workspace ID"
              value={auditFilters.workspace_id}
              onChange={(e) => setAuditFilters(prev => ({ ...prev, workspace_id: e.target.value }))}
              className="w-64"
            />
            <Input
              placeholder="Event type (e.g., document_uploaded)"
              value={auditFilters.event_type}
              onChange={(e) => setAuditFilters(prev => ({ ...prev, event_type: e.target.value }))}
              className="w-64"
            />
            <Button onClick={loadAuditLogs}>
              <Search className="w-4 h-4 mr-2" />
              Search Logs
            </Button>
          </div>

          {/* Logs Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Event Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Workspace</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{log.event_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-mono">
                        {log.workspace_id?.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {log.details ? JSON.stringify(log.details).slice(0, 50) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Failures Tab */}
        <TabsContent value="failures" className="space-y-6">
          <Button onClick={loadFailures} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Load Failures
          </Button>

          {failures.length > 0 ? (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Resource</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Workspace</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {failures.map((failure, idx) => (
                      <tr key={idx} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <Badge variant="destructive">{failure.type}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {failure.resource_name || failure.resource_id?.slice(0, 8)}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground font-mono">
                          {failure.workspace_id?.slice(0, 8)}...
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {failure.timestamp ? new Date(failure.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              loadWorkspaceDetail(failure.workspace_id);
                              setActiveTab("search");
                            }}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No failures found</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
